import {
  MAX_RECURRENCE_COUNT,
  RECURRENCE_FREQUENCIES,
  describeRecurrence,
  normalizeRecurrence,
  type LocalDateParts,
  type MonthlyRecurrenceMode,
  type RecurrenceFrequency,
  type RecurrenceRule,
} from '@hearth/shared';
import { clsx } from 'clsx';
import { useId, useState } from 'react';
import { Input, Select } from '../../components/ui';
import { toDateInput } from './entryModel';

const UNIT_LABELS: Record<RecurrenceFrequency, [string, string]> = {
  daily: ['day', 'days'],
  weekly: ['week', 'weeks'],
  monthly: ['month', 'months'],
  yearly: ['year', 'years'],
};

// ISO weekdays 1 (Monday) … 7 (Sunday); 2024-01-01 was a Monday.
const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7].map((weekday) => {
  const date = new Date(2024, 0, weekday);
  return {
    weekday,
    short: new Intl.DateTimeFormat('en-CA', { weekday: 'narrow' }).format(date),
    long: new Intl.DateTimeFormat('en-CA', { weekday: 'long' }).format(date),
  };
});

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, Math.round(value)));
}

export type RecurrenceEditorProps = {
  rule: RecurrenceRule;
  /** Local date of the first occurrence (the entry's start). */
  first: LocalDateParts;
  onChange: (rule: RecurrenceRule) => void;
};

/** Google's "Custom recurrence": every N days/weeks/months/years, weekdays, monthly mode and how it ends. */
export function RecurrenceEditor({ rule, first, onChange }: RecurrenceEditorProps) {
  const id = useId();
  // Typed as text so the field can be cleared while typing; committed clamped on blur.
  const [intervalText, setIntervalText] = useState(String(rule.interval));
  const [countText, setCountText] = useState(rule.ends.type === 'after' ? String(rule.ends.count) : '10');
  const startDate = new Date(first.year, first.month - 1, first.day);
  const update = (next: Partial<RecurrenceRule>) => onChange(normalizeRecurrence({ ...rule, ...next }, first));

  const setFreq = (freq: RecurrenceFrequency) =>
    onChange(
      normalizeRecurrence(
        {
          freq,
          interval: rule.interval,
          ends: rule.ends,
          byWeekday: freq === 'weekly' ? (rule.byWeekday ?? [first.weekday]) : undefined,
          monthlyMode: freq === 'monthly' ? (rule.monthlyMode ?? 'day_of_month') : undefined,
        },
        first,
      ),
    );

  const toggleWeekday = (weekday: number) => {
    const days = rule.byWeekday ?? [first.weekday];
    const next = days.includes(weekday) ? days.filter((day) => day !== weekday) : [...days, weekday];
    // A weekly rule needs at least one day.
    if (next.length > 0) update({ byWeekday: next });
  };

  const monthlyModes: MonthlyRecurrenceMode[] = ['day_of_month', 'weekday_of_month'];
  // Only a date in the month's final seven days is that month's last such weekday.
  const lastDayOfMonth = new Date(first.year, first.month, 0).getDate();
  if (first.day + 7 > lastDayOfMonth || rule.monthlyMode === 'last_weekday_of_month') monthlyModes.push('last_weekday_of_month');

  const endsOnDefault = toDateInput(new Date(first.year, first.month + 2, first.day));

  return (
    <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50/60 p-3 text-[13px]">
      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor={`${id}-interval`} className="text-slate-700">
          Repeat every
        </label>
        <Input
          id={`${id}-interval`}
          type="number"
          min={1}
          max={99}
          className="w-16"
          value={intervalText}
          onChange={(event) => {
            setIntervalText(event.target.value);
            const value = Number(event.target.value);
            if (event.target.value !== '' && Number.isFinite(value)) update({ interval: clamp(value, 1, 99) });
          }}
          onBlur={() => setIntervalText(String(rule.interval))}
        />
        <Select
          aria-label="Repeat unit"
          className="w-32"
          value={rule.freq}
          onChange={(event) => setFreq(event.target.value as RecurrenceFrequency)}
          options={RECURRENCE_FREQUENCIES.map((freq) => ({ value: freq, label: rule.interval === 1 ? UNIT_LABELS[freq][0] : UNIT_LABELS[freq][1] }))}
        />
      </div>

      {rule.freq === 'weekly' && (
        <div className="space-y-1.5">
          <p className="text-slate-700" id={`${id}-days`}>
            Repeat on
          </p>
          <div className="flex gap-1" role="group" aria-labelledby={`${id}-days`}>
            {WEEKDAYS.map(({ weekday, short, long }) => {
              const active = (rule.byWeekday ?? [first.weekday]).includes(weekday);
              return (
                <button
                  key={weekday}
                  type="button"
                  aria-pressed={active}
                  aria-label={long}
                  title={long}
                  onClick={() => toggleWeekday(weekday)}
                  className={clsx(
                    'size-7 rounded-full text-xs font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ember-500',
                    active ? 'bg-ember-600 text-white' : 'bg-surface text-slate-600 ring-1 ring-slate-300 hover:bg-slate-100',
                  )}
                >
                  {short}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {rule.freq === 'monthly' && (
        <Select
          aria-label="Monthly on"
          value={rule.monthlyMode ?? 'day_of_month'}
          onChange={(event) => update({ monthlyMode: event.target.value as MonthlyRecurrenceMode })}
          options={monthlyModes.map((mode) => ({ value: mode, label: describeRecurrence({ freq: 'monthly', monthlyMode: mode }, first) }))}
        />
      )}

      <fieldset className="space-y-2">
        <legend className="mb-1.5 text-slate-700">Ends</legend>
        <label className="flex cursor-pointer items-center gap-2 text-slate-800">
          <input
            type="radio"
            name={`${id}-ends`}
            className="size-4 accent-ember-600"
            checked={rule.ends.type === 'never'}
            onChange={() => update({ ends: { type: 'never' } })}
          />
          Never
        </label>
        <div className="flex items-center gap-2">
          <label className="flex w-16 cursor-pointer items-center gap-2 text-slate-800">
            <input
              type="radio"
              name={`${id}-ends`}
              className="size-4 accent-ember-600"
              checked={rule.ends.type === 'on'}
              onChange={() => update({ ends: { type: 'on', date: endsOnDefault } })}
            />
            On
          </label>
          <Input
            type="date"
            size="sm"
            aria-label="Last date"
            className="w-40"
            min={toDateInput(startDate)}
            disabled={rule.ends.type !== 'on'}
            value={rule.ends.type === 'on' ? rule.ends.date : endsOnDefault}
            onChange={(event) => {
              if (event.target.value) update({ ends: { type: 'on', date: event.target.value } });
            }}
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="flex w-16 cursor-pointer items-center gap-2 text-slate-800">
            <input
              type="radio"
              name={`${id}-ends`}
              className="size-4 accent-ember-600"
              checked={rule.ends.type === 'after'}
              onChange={() => update({ ends: { type: 'after', count: clamp(Number(countText) || 10, 1, MAX_RECURRENCE_COUNT) } })}
            />
            After
          </label>
          <Input
            type="number"
            size="sm"
            aria-label="Number of occurrences"
            min={1}
            max={MAX_RECURRENCE_COUNT}
            className="w-20"
            disabled={rule.ends.type !== 'after'}
            value={countText}
            onChange={(event) => {
              setCountText(event.target.value);
              const value = Number(event.target.value);
              if (event.target.value !== '' && Number.isFinite(value)) update({ ends: { type: 'after', count: clamp(value, 1, MAX_RECURRENCE_COUNT) } });
            }}
            onBlur={() => {
              if (rule.ends.type === 'after') setCountText(String(rule.ends.count));
            }}
          />
          <span className="text-slate-600">occurrences</span>
        </div>
      </fieldset>
    </div>
  );
}
